// Complete 78-card Rider-Waite Tarot Deck
export interface TarotCard {
  name: string;
  number: number;
  suit: string;
  keywords: string[];
  story: string;
  uprightMeaning: string;
  reversedMeaning: string;
  symbolism: string[];
  journalPrompts: string[];
}

export const TAROT_DECK: TarotCard[] = [
  // Major Arcana (0-21)
  {
    name: "The Fool",
    number: 0,
    suit: "Major Arcana",
    keywords: ["New beginnings", "Innocence", "Adventure", "Potential"],
    story: "A young person stands at the edge of a cliff, gazing at the sky with a small dog at their heels. They carry a bundle on a stick - all their worldly possessions. The white rose represents purity, the mountains symbolize challenges ahead. This fool isn't foolish - they're brave enough to begin despite not knowing the outcome.",
    uprightMeaning: "New adventures, taking a leap of faith, beginnings, spontaneity, innocence",
    reversedMeaning: "Recklessness, carelessness, distraction, naivety, foolishness",
    symbolism: ["White rose: purity", "Dog: loyalty", "Cliff: leap of faith", "Mountains: challenges"],
    journalPrompts: ["What new beginning am I being called toward?", "Where am I being asked to trust despite uncertainty?", "What would I do if I knew I couldn't fail?"]
  },
  {
    name: "The Magician",
    number: 1,
    suit: "Major Arcana",
    keywords: ["Manifestation", "Power", "Action", "Resourcefulness"],
    story: "A figure stands before a table holding all four suit symbols - the tools of creation. One hand points to heaven, one to earth, channeling divine power into the material world. The infinity symbol above their head shows unlimited potential. Red roses and white lilies grow around them - passion and purity combined.",
    uprightMeaning: "Manifestation, resourcefulness, power, inspired action, skill",
    reversedMeaning: "Manipulation, cunning, wasted talent, illusion, deception",
    symbolism: ["Infinity symbol: unlimited potential", "Four suits: mastery of all elements", "Red roses: passion", "White lilies: purity"],
    journalPrompts: ["What resources do I already have to create what I want?", "How can I channel my energy more effectively?", "What am I ready to manifest?"]
  },
  {
    name: "The High Priestess",
    number: 2,
    suit: "Major Arcana",
    keywords: ["Intuition", "Mystery", "Subconscious", "Divine feminine"],
    story: "She sits between two pillars marked B and J (Boaz and Jachin from Solomon's temple), guarding sacred knowledge. The Torah scroll on her lap represents hidden wisdom. Behind her, a veil decorated with pomegranates conceals deeper mysteries. She is the keeper of the unconscious, trusting inner knowing over outer noise.",
    uprightMeaning: "Intuition, sacred knowledge, divine feminine, subconscious mind",
    reversedMeaning: "Secrets, disconnection from intuition, silence, repression",
    symbolism: ["Two pillars: duality", "Pomegranates: fertility & the underworld", "Moon at feet: intuition", "Torah scroll: hidden knowledge"],
    journalPrompts: ["What does my intuition tell me that my mind refuses to hear?", "What secrets am I keeping from myself?", "Where do I need to trust my inner wisdom?"]
  },
  {
    name: "The Empress",
    number: 3,
    suit: "Major Arcana",
    keywords: ["Abundance", "Nurturing", "Fertility", "Nature"],
    story: "She reclines in nature's bounty, surrounded by lush grain and a forest. Her crown of twelve stars represents the zodiac and divine feminine. A heart-shaped shield with Venus's symbol leans nearby. A river flows behind her - emotions and creativity in constant motion. She is Mother Nature herself, abundant and life-giving.",
    uprightMeaning: "Abundance, nurturing, fertility, beauty, nature, creativity",
    reversedMeaning: "Creative block, dependence, smothering, emptiness, neglect",
    symbolism: ["Twelve stars: zodiac/cosmic connection", "Venus symbol: love & beauty", "Grain: abundance", "Flowing water: emotions"],
    journalPrompts: ["Where am I being called to nurture and create?", "How can I connect more deeply with abundance?", "What needs to be birthed through me?"]
  },
  {
    name: "The Emperor",
    number: 4,
    suit: "Major Arcana",
    keywords: ["Authority", "Structure", "Control", "Father figure"],
    story: "He sits on a stone throne decorated with ram heads (Aries energy), holding an ankh (Egyptian symbol of life) and orb (dominion). Mountains rise behind him - the challenge he's conquered. His red robes show passion channeled into structure. Where the Empress flows, the Emperor builds, creates order, establishes kingdoms.",
    uprightMeaning: "Authority, establishment, structure, father figure, leadership",
    reversedMeaning: "Domination, excessive control, rigidity, inflexibility, tyranny",
    symbolism: ["Ram heads: Aries/leadership", "Ankh: life force", "Orb: worldly power", "Mountains: conquered challenges"],
    journalPrompts: ["Where do I need more structure in my life?", "How am I being called to lead?", "What foundations need strengthening?"]
  },
  {
    name: "The Hierophant",
    number: 5,
    suit: "Major Arcana",
    keywords: ["Tradition", "Conformity", "Spiritual wisdom", "Institutions"],
    story: "A religious figure sits between two pillars, blessing two followers. He wears three crowns representing the conscious, subconscious, and superconscious. His right hand makes the sign of benediction, his left holds a papal cross. He represents traditional institutions, spiritual teachings, and the wisdom passed down through generations.",
    uprightMeaning: "Tradition, conformity, education, belief systems, institutions",
    reversedMeaning: "Rebellion, subversiveness, new approaches, freedom",
    symbolism: ["Three crowns: three realms of consciousness", "Two followers: teaching", "Papal cross: spiritual authority", "Keys: access to hidden knowledge"],
    journalPrompts: ["What traditions serve me? Which limit me?", "Where do I seek approval from external authorities?", "What spiritual teachings resonate with my truth?"]
  },
  {
    name: "The Lovers",
    number: 6,
    suit: "Major Arcana",
    keywords: ["Love", "Harmony", "Relationships", "Choices"],
    story: "A naked man and woman stand in Eden beneath an angel (Raphael). Behind the woman is the tree of knowledge with a serpent, behind the man the tree of life. This isn't just romantic love - it's the choice between two paths, the harmony of opposites, the divine blessing on union. It's about values, relationships, and conscious choices.",
    uprightMeaning: "Love, harmony, relationships, values alignment, choices",
    reversedMeaning: "Self-love issues, disharmony, imbalance, misalignment of values",
    symbolism: ["Angel Raphael: divine blessing", "Tree of knowledge: conscious choice", "Tree of life: eternal growth", "Nakedness: vulnerability"],
    journalPrompts: ["What choice is before me? What are my values guiding me toward?", "Where am I being called to deeper union?", "What relationships reflect my highest values?"]
  },
  {
    name: "The Chariot",
    number: 7,
    suit: "Major Arcana",
    keywords: ["Willpower", "Victory", "Determination", "Control"],
    story: "A warrior rides a chariot pulled by two sphinxes - one black, one white. He wears a crown of stars and holds no reins, controlling the sphinxes through sheer willpower. A city lies behind him - what he's leaving. He's moving forward through force of will, mastering opposing forces to achieve victory.",
    uprightMeaning: "Control, willpower, success, determination, victory",
    reversedMeaning: "Lack of direction, opposition, lack of control",
    symbolism: ["Two sphinxes: opposing forces", "No reins: willpower", "Star crown: divine guidance", "Square on chest: grounded in earth"],
    journalPrompts: ["Where do I need to assert my will?", "What opposing forces within me need to work together?", "What victory am I moving toward?"]
  },
  {
    name: "Strength",
    number: 8,
    suit: "Major Arcana",
    keywords: ["Courage", "Inner strength", "Compassion", "Patience"],
    story: "A woman gently closes a lion's mouth with her bare hands. She wears white robes of purity and an infinity symbol above her head. This isn't about brute force - it's about the strength of compassion, patience, and inner power. She tames the beast through love, not violence.",
    uprightMeaning: "Strength, courage, patience, compassion, soft control",
    reversedMeaning: "Self-doubt, weakness, insecurity, lack of courage",
    symbolism: ["Lion: primal instincts", "Gentle touch: compassion over force", "Infinity symbol: unlimited inner power", "White robes: purity of intent"],
    journalPrompts: ["Where do I need to meet myself with more compassion?", "What part of me needs gentle taming rather than harsh control?", "How can I approach challenges with soft power?"]
  },
  {
    name: "The Hermit",
    number: 9,
    suit: "Major Arcana",
    keywords: ["Introspection", "Solitude", "Inner guidance", "Wisdom"],
    story: "An old man stands alone on a mountain peak, holding a lantern containing a six-pointed star. He uses a staff for support. He's reached the summit after a long journey and now holds up his light to guide others. This is the wisdom that comes from solitude, introspection, and inner work.",
    uprightMeaning: "Soul-searching, introspection, inner guidance, solitude",
    reversedMeaning: "Isolation, loneliness, withdrawal, lost your way",
    symbolism: ["Lantern: inner light", "Six-pointed star: wisdom", "Mountain peak: spiritual heights", "Staff: support from inner work"],
    journalPrompts: ["What wisdom am I discovering in solitude?", "Where do I need to retreat to hear my inner voice?", "What light am I meant to hold up for others?"]
  },
  {
    name: "Wheel of Fortune",
    number: 10,
    suit: "Major Arcana",
    keywords: ["Change", "Cycles", "Fate", "Turning point"],
    story: "A great wheel turns in the sky, with figures rising and falling around it. At the four corners sit four winged creatures reading books - representing the four fixed signs of the zodiac. Hebrew letters spell YHVH (God's name). The wheel reminds us that all things are cyclical - what goes up must come down, and vice versa.",
    uprightMeaning: "Good luck, karma, life cycles, destiny, turning point",
    reversedMeaning: "Bad luck, resistance to change, breaking cycles",
    symbolism: ["Wheel: cycles of life", "YHVH: divine name", "Snake: descending into material", "Anubis: ascending to spiritual"],
    journalPrompts: ["What cycle am I in right now?", "How am I being asked to flow with change rather than resist it?", "What karma am I working through?"]
  },
  {
    name: "Justice",
    number: 11,
    suit: "Major Arcana",
    keywords: ["Fairness", "Truth", "Cause and effect", "Law"],
    story: "A figure sits between two pillars, holding scales in one hand and a sword in the other. She wears a red robe and a crown. This is cosmic justice - the law of cause and effect, karma, accountability. The sword cuts through illusions, the scales weigh truth. She reminds us that our actions have consequences.",
    uprightMeaning: "Justice, fairness, truth, cause and effect, law",
    reversedMeaning: "Unfairness, lack of accountability, dishonesty, legal issues",
    symbolism: ["Scales: balance & fairness", "Sword: truth cuts through illusion", "Purple veil: compassion", "Red robe: passion for justice"],
    journalPrompts: ["Where do I need to be more accountable?", "What truth have I been avoiding?", "How can I restore balance?"]
  },
  {
    name: "The Hanged Man",
    number: 12,
    suit: "Major Arcana",
    keywords: ["Surrender", "Letting go", "New perspective", "Sacrifice"],
    story: "A man hangs upside down from a tree, but his face is peaceful. His leg forms the number 4, his body a cross. A halo glows around his head. He's not a victim - he chose to hang there. Sometimes we must surrender, let go of control, see from a completely different angle. Wisdom comes from suspension, from giving up what we thought we knew.",
    uprightMeaning: "Surrender, new perspective, letting go, spiritual growth",
    reversedMeaning: "Resistance, stalling, indecision, stagnation",
    symbolism: ["Upside down: new perspective", "Peaceful face: willing surrender", "Halo: enlightenment through surrender", "Living tree: growth through sacrifice"],
    journalPrompts: ["What am I being asked to surrender?", "How would this situation look from a completely different perspective?", "What wisdom comes from letting go of control?"]
  },
  {
    name: "Death",
    number: 13,
    suit: "Major Arcana",
    keywords: ["Transformation", "Endings", "Transition", "Release"],
    story: "A skeletal figure in black armor rides a white horse, carrying a black flag with a white rose. People kneel before him - a king, a child, a bishop. The sun rises between two towers in the distance. Death isn't the end - it's transformation. What dies makes space for rebirth. The sun always rises after the darkest night.",
    uprightMeaning: "Transformation, endings, change, transition, release",
    reversedMeaning: "Resistance to change, stagnation, fear of endings",
    symbolism: ["White horse: purity of transformation", "White rose: beauty in endings", "Sunrise: rebirth follows death", "River: flow of life continues"],
    journalPrompts: ["What needs to die so something new can be born?", "How am I resisting necessary transformation?", "What beauty exists in this ending?"]
  },
  {
    name: "Temperance",
    number: 14,
    suit: "Major Arcana",
    keywords: ["Balance", "Moderation", "Patience", "Purpose"],
    story: "An angel with one foot in water, one on land, pours liquid between two cups. A path winds between mountains toward a glowing crown. The angel's name is Raphael - the healer. She shows us the middle way, the art of balance, the patience required to blend opposites into something greater. Healing comes through moderation and purpose.",
    uprightMeaning: "Balance, moderation, patience, purpose, meaning",
    reversedMeaning: "Imbalance, excess, lack of vision, self-indulgence",
    symbolism: ["Pouring between cups: alchemy & balance", "One foot in water, one on land: balancing emotional & practical", "Triangle in square: divine manifest in material", "Path to crown: journey to higher purpose"],
    journalPrompts: ["Where do I need more balance?", "What extremes am I being called to moderate?", "How can I blend opposing parts of myself?"]
  },
  {
    name: "The Devil",
    number: 15,
    suit: "Major Arcana",
    keywords: ["Bondage", "Materialism", "Addiction", "Restriction"],
    story: "A horned figure sits on a throne, with a naked man and woman chained before him - echoes of The Lovers. But look closely - the chains are loose. They could slip them off. The Devil represents self-imposed bondage, addiction, materialism, the lies we tell ourselves about powerlessness. The truth? We hold the key to our own freedom.",
    uprightMeaning: "Bondage, addiction, materialism, playfulness, sexuality",
    reversedMeaning: "Freedom, release, restoring control, detachment",
    symbolism: ["Loose chains: self-imposed bondage", "Inverted pentagram: material over spiritual", "Torch: false light/illusion", "Echoes Lovers: shadow side of relationship"],
    journalPrompts: ["Where have I given away my power?", "What chains could I remove but choose not to?", "What addiction or pattern holds me?"]
  },
  {
    name: "The Tower",
    number: 16,
    suit: "Major Arcana",
    keywords: ["Upheaval", "Sudden change", "Revelation", "Breakdown"],
    story: "Lightning strikes a crown-topped tower, and two figures fall from the burning structure. This is the moment illusions shatter. What you built on false foundations crumbles. It looks like disaster but it's divine intervention - clearing what must go so truth can emerge. Destruction that precedes reconstruction.",
    uprightMeaning: "Sudden change, upheaval, chaos, revelation, awakening",
    reversedMeaning: "Avoiding disaster, fear of change, resisting transformation",
    symbolism: ["Lightning: divine intervention", "Crown falling: ego destruction", "Two figures: structures of false self", "Flames: purification"],
    journalPrompts: ["What false structures in my life are ready to fall?", "What truth is being revealed through this destruction?", "How is this upheaval serving my highest good?"]
  },
  {
    name: "The Star",
    number: 17,
    suit: "Major Arcana",
    keywords: ["Hope", "Faith", "Renewal", "Inspiration"],
    story: "After the Tower's destruction, a naked woman kneels by water, pouring from two jugs - one onto land, one into water. Above her, eight stars shine (seven small, one large). She is vulnerable, exposed, yet hopeful. This is raw faith and renewal. After everything falls apart, the Star reminds us that hope remains, inspiration flows, and healing comes.",
    uprightMeaning: "Hope, faith, purpose, renewal, spirituality, inspiration",
    reversedMeaning: "Lack of faith, despair, disconnection, discouragement",
    symbolism: ["Eight stars: hope after destruction", "Naked: vulnerability & authenticity", "Pouring water: renewal flowing", "Bird in tree: higher perspective"],
    journalPrompts: ["What hope remains even after loss?", "How am I being renewed?", "What authentic truth am I revealing?"]
  },
  {
    name: "The Moon",
    number: 18,
    suit: "Major Arcana",
    keywords: ["Illusion", "Fear", "Anxiety", "Subconscious"],
    story: "A full moon hangs in the sky as a wolf and dog howl. A crayfish emerges from water onto a path between two towers. Everything is unclear, dreamlike, confusing. This is the realm of illusions, fears, anxieties. The Moon shows us the shadow work required, the journey through uncertainty. Trust your intuition even when you can't see clearly.",
    uprightMeaning: "Illusion, fear, anxiety, subconscious, intuition",
    reversedMeaning: "Clarity, release of fear, seeing through illusions",
    symbolism: ["Wolf & dog: tamed & wild instincts", "Crayfish: emerging from unconscious", "Towers: gateway between worlds", "Winding path: unclear journey"],
    journalPrompts: ["What fears or illusions cloud my path?", "What is my intuition telling me despite the uncertainty?", "What shadow work is calling me?"]
  },
  {
    name: "The Sun",
    number: 19,
    suit: "Major Arcana",
    keywords: ["Success", "Joy", "Celebration", "Positivity"],
    story: "A naked child rides a white horse under a blazing sun, holding a red banner. Sunflowers grow behind them. After the confusion of the Moon, clarity returns. This is pure joy, success, vitality, the light of consciousness. The child represents innocence regained, authenticity, freedom. Everything is illuminated. Life celebrates itself.",
    uprightMeaning: "Success, joy, celebration, positivity, vitality",
    reversedMeaning: "Temporary depression, lack of success, sadness",
    symbolism: ["Blazing sun: clarity & consciousness", "Naked child: innocence & authenticity", "White horse: purity", "Sunflowers: turning toward light"],
    journalPrompts: ["What am I celebrating?", "Where is success blooming in my life?", "How can I embrace more joy and authenticity?"]
  },
  {
    name: "Judgement",
    number: 20,
    suit: "Major Arcana",
    keywords: ["Reflection", "Reckoning", "Awakening", "Rebirth"],
    story: "An angel blows a trumpet, and the dead rise from their coffins with arms uplifted. This is the moment of reckoning, of seeing yourself clearly, of answering your calling. It's awakening to your true purpose, rising to meet your highest potential. Past, present, and future converge. You are being called to your next level of evolution.",
    uprightMeaning: "Reflection, reckoning, inner calling, absolution, rebirth",
    reversedMeaning: "Self-doubt, refusal to learn lessons, self-loathing",
    symbolism: ["Angel's trumpet: divine call", "Rising dead: spiritual awakening", "Flag with cross: redemption", "Mountains in distance: spiritual heights ahead"],
    journalPrompts: ["What is my soul calling me toward?", "How have I evolved through my experiences?", "What version of myself is ready to rise?"]
  },
  {
    name: "The World",
    number: 21,
    suit: "Major Arcana",
    keywords: ["Completion", "Accomplishment", "Travel", "Success"],
    story: "A naked figure dances within a laurel wreath, holding two wands. The four corners show the same four creatures from the Wheel of Fortune. She's completed the journey through the Major Arcana. This is fulfillment, wholeness, the end of one cycle and the readiness for the next. You've learned the lessons. You've integrated the wisdom. You're complete.",
    uprightMeaning: "Completion, accomplishment, travel, fulfillment",
    reversedMeaning: "Incompletion, seeking closure, unfinished business",
    symbolism: ["Laurel wreath: victory & completion", "Four corners: mastery of all elements", "Dancing figure: celebration & wholeness", "Purple veil: spiritual wisdom"],
    journalPrompts: ["What cycle am I completing?", "What have I mastered?", "How am I whole, exactly as I am?"]
  },
  
  // Wands (23 cards including Page, Knight, Queen, King)
  {
    name: "Ace of Wands",
    number: 1,
    suit: "Wands",
    keywords: ["Inspiration", "New opportunity", "Growth", "Potential"],
    story: "A hand emerges from a cloud, offering a wooden staff. Leaves sprout from it - life force itself. In the distance, a castle on a hill. This is the spark of inspiration, the seed of potential, creative fire. It's the beginning of a passionate journey, an idea whose time has come.",
    uprightMeaning: "Inspiration, new opportunities, growth, potential",
    reversedMeaning: "Lack of direction, delays, poor planning, uninspiring",
    symbolism: ["Hand from cloud: divine inspiration", "Sprouting leaves: growth potential", "Castle: goals to achieve", "Wand: creative power"],
    journalPrompts: ["What new passion is igniting in me?", "Where do I feel inspired to take action?", "What creative project wants to be born?"]
  },
  {
    name: "Two of Wands",
    number: 2,
    suit: "Wands",
    keywords: ["Planning", "Future", "Progress", "Decisions"],
    story: "A person stands between two wands, holding a globe in their hand. They look out from a castle over land and sea. They've achieved something but know there's more. This is the planning stage - envisioning the future, weighing options, preparing for the next phase of expansion.",
    uprightMeaning: "Planning, making decisions, leaving comfort zone, future planning",
    reversedMeaning: "Fear of unknown, lack of planning, poor organization",
    symbolism: ["Globe: world of possibilities", "Castle: established position", "Two wands: choices", "Ocean view: emotional exploration ahead"],
    journalPrompts: ["What future am I envisioning?", "What choices lie before me?", "How can I plan for my next expansion?"]
  },
  {
    name: "Three of Wands",
    number: 3,
    suit: "Wands",
    keywords: ["Expansion", "Foresight", "Overseas opportunities", "Progress"],
    story: "A figure stands on a cliff watching three ships sail away. Three wands are planted firmly beside them. They've sent their ventures out into the world and now await the results. This is expansion, long-term planning, foresight. The hard work is done; now comes the anticipation.",
    uprightMeaning: "Progress, expansion, foresight, overseas opportunities",
    reversedMeaning: "Obstacles, frustrations, delays, lack of foresight",
    symbolism: ["Three ships: ventures launched", "Three wands: stability", "Cliff: higher perspective", "Ocean: emotional investment in outcome"],
    journalPrompts: ["What have I set in motion?", "Where am I expanding beyond my current horizons?", "What am I waiting to return to me?"]
  },
  {
    name: "Four of Wands",
    number: 4,
    suit: "Wands",
    keywords: ["Celebration", "Harmony", "Marriage", "Home"],
    story: "Four wands create an archway decorated with flowers. People celebrate beneath it. In the distance, a castle. This is achievement worth celebrating - a homecoming, a wedding, a harvest. It's stability, joy, the fruits of labor. Community comes together in celebration.",
    uprightMeaning: "Celebration, harmony, marriage, home, community",
    reversedMeaning: "Lack of support, transience, home conflicts, delayed celebrations",
    symbolism: ["Four wands: stable foundation", "Flower garlands: celebration", "Castle: home & security", "Celebrating people: community"],
    journalPrompts: ["What am I celebrating?", "How can I honor this achievement?", "What foundation have I built?"]
  },
  {
    name: "Five of Wands",
    number: 5,
    suit: "Wands",
    keywords: ["Conflict", "Competition", "Disagreements", "Struggle"],
    story: "Five figures clash with their wands raised. No one seems seriously hurt - this is competition, not war. Ideas clashing, egos competing, everyone talking and no one listening. This is the friction that can lead to breakthroughs or breakdowns, depending on how it's handled.",
    uprightMeaning: "Conflict, competition, tension, diversity of opinions",
    reversedMeaning: "Avoiding conflict, inner conflict, end of competition",
    symbolism: ["Five wands: many perspectives", "Clashing: creative friction", "No real damage: growth through conflict", "Clear sky: clarity possible"],
    journalPrompts: ["Where is conflict showing me what I truly value?", "How can I see competition as collaboration?", "What truth emerges from this friction?"]
  },
  {
    name: "Six of Wands",
    number: 6,
    suit: "Wands",
    keywords: ["Victory", "Success", "Public recognition", "Pride"],
    story: "A victor rides through a crowd on a white horse, holding a wand with a laurel wreath. People wave their own wands in celebration. This is success, recognition, the moment of triumph. You've won. The crowd acknowledges your achievement. Enjoy this victory.",
    uprightMeaning: "Success, public recognition, victory, self-confidence",
    reversedMeaning: "Lack of recognition, no confidence, egotism, fall from grace",
    symbolism: ["Laurel wreath: victory", "White horse: purity of achievement", "Crowd: public recognition", "Wands raised: celebration"],
    journalPrompts: ["What victory am I celebrating?", "How does recognition feel?", "Where do I need to acknowledge my own success?"]
  },
  {
    name: "Seven of Wands",
    number: 7,
    suit: "Wands",
    keywords: ["Challenge", "Defense", "Perseverance", "Maintaining position"],
    story: "A figure stands on high ground, defending their position against six wands thrust up from below. They look determined but weary. Success has made you a target. Others challenge your position. This is about standing your ground, defending what you've built, persevering despite opposition.",
    uprightMeaning: "Challenge, competition, perseverance, defending position",
    reversedMeaning: "Exhaustion, giving up, overwhelmed, feeling vulnerable",
    symbolism: ["High ground: advantage", "Six challengers: opposition", "Single defender: standing alone", "Mismatched shoes: unpreparedness"],
    journalPrompts: ["What am I defending?", "Where do I need to stand my ground?", "Is this battle worth fighting?"]
  },
  {
    name: "Eight of Wands",
    number: 8,
    suit: "Wands",
    keywords: ["Speed", "Swift action", "Air travel", "Movement"],
    story: "Eight wands fly through clear air over a river and green countryside. No obstacles, just forward movement. This is things happening quickly - messages arriving, travel, rapid developments. Everything accelerates. The path is clear. Move fast.",
    uprightMeaning: "Speed, action, air travel, movement, quick decisions",
    reversedMeaning: "Delays, frustrations, resisting change, internal alignment needed",
    symbolism: ["Flying wands: rapid movement", "Clear air: no obstacles", "River: emotional flow", "Green land: fertile outcomes ahead"],
    journalPrompts: ["What is moving quickly in my life right now?", "Where do I need to act swiftly?", "Am I ready for this speed?"]
  },
  {
    name: "Nine of Wands",
    number: 9,
    suit: "Wands",
    keywords: ["Resilience", "Persistence", "Last stand", "Boundaries"],
    story: "A wounded figure leans on a wand, eight more wands standing behind them like a fence. They're battered but undefeated, vigilant, ready for one more challenge. This is persistence when you're exhausted, the courage to stand your ground one more time, boundaries maintained despite pressure.",
    uprightMeaning: "Resilience, persistence, test of faith, boundaries, courage",
    reversedMeaning: "Exhaustion, giving up, lacking courage, paranoia",
    symbolism: ["Wounded warrior: battle-tested", "Eight wands fence: strong boundaries", "Bandaged head: healing while defending", "Determination: won't give up"],
    journalPrompts: ["What am I still defending despite exhaustion?", "Where do my boundaries need reinforcement?", "What gives me strength to persist?"]
  },
  {
    name: "Ten of Wands",
    number: 10,
    suit: "Wands",
    keywords: ["Burden", "Responsibility", "Hard work", "Completion near"],
    story: "A figure carries ten wands toward a distant house, bent under their weight. The finish line is visible but the load is heavy. This is taking on too much, being almost there, the final push before completion. Success comes with weight. Are you ready to put some burdens down?",
    uprightMeaning: "Burden, responsibility, hard work, stress, achievement",
    reversedMeaning: "Refusing responsibility, inability to delegate, collapse under stress",
    symbolism: ["Ten wands: maximum load", "Bent figure: weight of responsibility", "Distant house: goal in sight", "Heavy load: success has costs"],
    journalPrompts: ["What burdens am I carrying that aren't mine?", "Where do I need to delegate or release?", "What am I so close to completing?"]
  },
  {
    name: "Page of Wands",
    number: 11,
    suit: "Wands",
    keywords: ["Enthusiasm", "Exploration", "Discovery", "Free spirit"],
    story: "A young person holds a wand, gazing at it with wonder. Mountains rise in the distance - adventures ahead. They're dressed for travel, ready to explore. This is the messenger of passion, the beginning of creative ventures, youthful enthusiasm, and the courage to try new things.",
    uprightMeaning: "Enthusiasm, exploration, discovery, free spirit, good news",
    reversedMeaning: "Lack of direction, procrastination, creating conflict, boring",
    symbolism: ["Wand examined: curiosity", "Mountains: adventures ahead", "Desert: unexplored territory", "Salamanders: transformation"],
    journalPrompts: ["What new passion wants to be explored?", "Where can I bring more playful curiosity?", "What message is my enthusiasm trying to deliver?"]
  },
  {
    name: "Knight of Wands",
    number: 12,
    suit: "Wands",
    keywords: ["Energy", "Passion", "Adventure", "Impulsiveness"],
    story: "A knight charges forward on a rearing horse, wand raised high. His armor decorated with salamanders (transformation through fire). He's all action, energy, passion - but sometimes reckless. This is adventurous spirit, following passion wherever it leads, taking risks, embracing the journey.",
    uprightMeaning: "Energy, passion, lust, action, adventure, impulsiveness",
    reversedMeaning: "Passion without direction, haste, recklessness, volatility",
    symbolism: ["Charging horse: forward momentum", "Raised wand: leading with passion", "Salamanders: transformation", "Desert landscape: uncharted territory"],
    journalPrompts: ["Where am I being called to bold action?", "What passion drives me forward?", "Am I moving with purpose or just speed?"]
  },
  {
    name: "Queen of Wands",
    number: 13,
    suit: "Wands",
    keywords: ["Confidence", "Independence", "Determination", "Charisma"],
    story: "A queen sits on a throne decorated with lions and sunflowers. She holds a wand and a sunflower, a black cat at her feet. She's confident, magnetic, independent. She's turned her passion into mastery, her fire into sustainable warmth. She commands attention naturally, leads with charisma.",
    uprightMeaning: "Confidence, independence, determination, charisma, self-respect",
    reversedMeaning: "Demanding, overbearing, jealousy, selfishness, vengeful",
    symbolism: ["Lions: courage & strength", "Sunflowers: turning toward light", "Black cat: intuition", "Wand & sunflower: balancing action & being"],
    journalPrompts: ["Where do I need more confidence?", "How can I lead with charisma?", "What makes me magnetic?"]
  },
  {
    name: "King of Wands",
    number: 14,
    suit: "Wands",
    keywords: ["Leadership", "Vision", "Entrepreneurship", "Honour"],
    story: "A king sits on a throne decorated with lions and salamanders, holding a wand while gazing into the distance. His cape is covered with salamanders. He's the master of fire - visionary, leader, entrepreneur. He sees the big picture, inspires others, turns passion into legacy. Natural-born leader.",
    uprightMeaning: "Leadership, vision, entrepreneurship, honour, natural-born leader",
    reversedMeaning: "Impulsiveness, haste, ruthlessness, high expectations",
    symbolism: ["Lions & salamanders: mastered fire", "Forward gaze: vision", "Wand: creative power wielded", "Orange robe: sustained passion"],
    journalPrompts: ["How am I being called to lead?", "What is my vision?", "Where can I inspire others through my passion?"]
  },

  // Cups (14 cards)
  {
    name: "Ace of Cups",
    number: 1,
    suit: "Cups",
    keywords: ["New love", "Emotional beginning", "Intuition", "Compassion"],
    story: "A chalice overflows with five streams, held by a divine hand emerging from clouds. A dove carrying a communion wafer descends toward it. Below, a lotus pond blooms. This is the gift of new emotional and spiritual beginnings - love offered, intuition awakening, the heart opening to divine grace.",
    uprightMeaning: "New feelings, love, intuition, emotional abundance, spiritual connection",
    reversedMeaning: "Emotional loss, blocked creativity, emptiness, repressed emotions",
    symbolism: ["Overflowing cup: abundance of feeling", "Dove: Holy Spirit/peace", "Lotus: spiritual awakening", "Five streams: five senses blessed"],
    journalPrompts: ["What new feeling is emerging?", "How is my heart opening?", "What emotional gift is being offered?"]
  },
  // ... (continue with all remaining Cups cards - Two through King)
  
  // Swords (14 cards)
  {
    name: "Ace of Swords",
    number: 1,
    suit: "Swords",
    keywords: ["Clarity", "Truth", "Mental breakthrough", "Justice"],
    story: "An upright sword pierces through a crown topped with laurel and olive branches. Mountains rise in the distance. This is the sword of truth cutting through confusion, the mental breakthrough, the moment of crystal clarity. It brings justice but also the responsibility to wield truth wisely.",
    uprightMeaning: "Breakthrough, clarity, truth, mental power, success",
    reversedMeaning: "Confusion, chaos, lack of clarity, misuse of power",
    symbolism: ["Crown: victory through mind", "Laurel: success", "Olive: peace", "Mountains: mental challenges"],
    journalPrompts: ["What truth is cutting through my confusion?", "Where do I need mental clarity?", "How can I use this insight wisely?"]
  },
  // ... (continue with remaining Swords)
  
  // Pentacles (14 cards)
  {
    name: "Ace of Pentacles",
    number: 1,
    suit: "Pentacles",
    keywords: ["Opportunity", "Prosperity", "New venture", "Manifestation"],
    story: "A hand emerges from a cloud, offering a golden pentacle. Below, a garden path leads through an archway of flowers to distant mountains. This is opportunity made manifest - a new job, financial beginning, material foundation. The seed of prosperity, the chance to build something lasting.",
    uprightMeaning: "Opportunity, prosperity, new venture, manifestation, abundance",
    reversedMeaning: "Lost opportunity, lack of planning, poor financial management",
    symbolism: ["Golden pentacle: material opportunity", "Garden: cultivated prosperity", "Archway: threshold", "Mountains: long-term goals"],
    journalPrompts: ["What opportunity is being offered?", "How can I manifest abundance?", "What foundation am I building?"]
  },
  // ... (continue with remaining Pentacles)
];
